import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { useAuth } from '../context/AuthContext';

interface TimeSeriesPoint {
  date: string;
  audits: number;
  positive: number;
  negative: number;
  neutral: number;
  positiveRate: number;
}

interface SectionBreakdown {
  section: string;
  positive: number;
  negative: number;
  neutral: number;
  positiveRate: number;
}

interface QuestionBreakdown {
  question: string;
  section: string;
  positive: number;
  negative: number;
  neutral: number;
  positiveRate: number;
  sourceField?: string | null;
}

interface AuditorSummary {
  auditedBy: string;
  audits: number;
  positiveRate: number;
  averageNegatives: number;
  negative: number;
  positive: number;
  lastAuditAt?: string | null;
}

interface DocumentSummary {
  missingDocuments: number;
  notGenerated: number;
  templateVersions: Record<string, number>;
  auditsWithNeutralAnswers: number;
  documentCoverage: number;
}

interface NegativeFinding {
  auditId: string;
  claimId: string;
  auditedBy: string;
  auditedAt: string;
  negativeAnswers: number;
  notes?: string;
  topFindings: Array<{
    question: string;
    section: string;
    answer: string;
  }>;
}

interface AuditAnalysisResponse {
  generatedAt: string;
  filters: {
    startDate?: string | null;
    endDate?: string | null;
    auditor?: string | null;
  };
  totals: {
    audits: number;
    questions: number;
    positive: number;
    negative: number;
    neutral: number;
    positiveRate: number;
    averageNegativesPerAudit: number;
  };
  timeSeries: TimeSeriesPoint[];
  sections: SectionBreakdown[];
  questions: QuestionBreakdown[];
  auditors: AuditorSummary[];
  documents: DocumentSummary;
  topFindings: NegativeFinding[];
  counts: {
    totalAudits: number;
    totalQuestions: number;
  };
  _cache_metadata?: {
    cache_age_seconds?: number;
    generated_at?: string;
  };
}

interface NegativeAnswerRow {
  auditId: string;
  claimId: string;
  auditedBy: string;
  auditedAt: string;
  question: string;
  section: string;
  answer: string;
  notes?: string;
}

interface AuditAnalysisNegativesResponse {
  generatedAt: string;
  filters: AuditAnalysisResponse['filters'];
  count: number;
  rows: NegativeAnswerRow[];
  summary?: {
    totalClaims: number;
    claims: string[];
  };
}

interface FiltersState {
  startDate: string;
  endDate: string;
  auditor: string;
}

const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;
const formatDateLabel = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });

const AuditAnalysis: React.FC = () => {
  const { apiRequest } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState<AuditAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [filters, setFilters] = useState<FiltersState>({ startDate: '', endDate: '', auditor: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'in-progress' | 'completed' | 'error'>('idle');
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');
  const [exportError, setExportError] = useState('');
  const [exportDownloadUrl, setExportDownloadUrl] = useState<string | null>(null);
  const [exportMode, setExportMode] = useState<'full' | 'negatives'>('full');
  const exportTimerRef = useRef<number | null>(null);

  const fetchData = async (override?: FiltersState) => {
    try {
      const activeFilters = override || filters;
      setLoading(!data);
      setIsRefreshing(!!data);
      setError('');

      const params = new URLSearchParams();
      if (activeFilters.startDate) params.append('start_date', activeFilters.startDate);
      if (activeFilters.endDate) params.append('end_date', activeFilters.endDate);
      if (activeFilters.auditor) params.append('auditor', activeFilters.auditor);

      const hasFilters = !!(activeFilters.startDate || activeFilters.endDate || activeFilters.auditor);
      const basePath = hasFilters ? '/ui/admin/analytics/audit-analysis' : '/ui/admin/analytics/audit-analysis/cached';
      const url = `${basePath}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiRequest<AuditAnalysisResponse>({ url, method: 'GET' });
      setData(response);
    } catch (err: any) {
      console.error('Failed to load audit analysis', err);
      setError(err?.message || 'Failed to load audit analysis');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableAuditors = useMemo(() => {
    if (!data) return [];
    return data.auditors.map(item => item.auditedBy).sort();
  }, [data]);

  const handleFilterChange = (field: keyof FiltersState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  const applyFilters = () => {
    fetchData(filters);
  };

  const clearFilters = () => {
    const cleared = { startDate: '', endDate: '', auditor: '' };
    setFilters(cleared);
    fetchData(cleared);
  };

  useEffect(() => {
    return () => {
      if (exportTimerRef.current) {
        window.clearInterval(exportTimerRef.current);
      }
      if (exportDownloadUrl) {
        URL.revokeObjectURL(exportDownloadUrl);
      }
    };
  }, [exportDownloadUrl]);

  const buildCsv = (analysis: AuditAnalysisResponse) => {
    const escape = (value: string | number | undefined | null) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const rows: string[] = [];
    const pushRow = (...cols: Array<string | number | undefined | null>) => {
      rows.push(cols.map(escape).join(','));
    };

    pushRow('Audit Analysis Export', new Date(analysis.generatedAt).toISOString());
    if (analysis.filters.startDate || analysis.filters.endDate || analysis.filters.auditor) {
      pushRow('Filters', `start=${analysis.filters.startDate || 'n/a'}`, `end=${analysis.filters.endDate || 'n/a'}`, `auditor=${analysis.filters.auditor || 'all'}`);
    }
    rows.push('');
    pushRow('Metric', 'Value');
    pushRow('Total audits', analysis.totals.audits);
    pushRow('Total questions', analysis.totals.questions);
    pushRow('Positive answers', analysis.totals.positive);
    pushRow('Negative answers', analysis.totals.negative);
    pushRow('Neutral answers', analysis.totals.neutral);
    pushRow('Positive rate', `${(analysis.totals.positiveRate * 100).toFixed(1)}%`);
    pushRow('Average negatives per audit', analysis.totals.averageNegativesPerAudit.toFixed(2));
    rows.push('');

    pushRow('Date', 'Audits', 'Positive', 'Negative', 'Neutral', 'Positive rate');
    analysis.timeSeries.forEach(item => {
      pushRow(item.date, item.audits, item.positive, item.negative, item.neutral, `${(item.positiveRate * 100).toFixed(1)}%`);
    });
    rows.push('');

    pushRow('Section', 'Positive', 'Negative', 'Neutral', 'Positive rate');
    analysis.sections.forEach(section => {
      pushRow(section.section, section.positive, section.negative, section.neutral, `${(section.positiveRate * 100).toFixed(1)}%`);
    });
    rows.push('');

    pushRow('Question', 'Section', 'Positive', 'Negative', 'Neutral', 'Positive rate');
    analysis.questions.forEach(question => {
      pushRow(question.question, question.section, question.positive, question.negative, question.neutral, `${(question.positiveRate * 100).toFixed(1)}%`);
    });
    rows.push('');

    pushRow('Auditor', 'Audits', 'Positive answers', 'Negative answers', 'Positive rate', 'Average negatives per audit', 'Last audit');
    analysis.auditors.forEach(auditor => {
      pushRow(
        auditor.auditedBy,
        auditor.audits,
        auditor.positive,
        auditor.negative,
        `${(auditor.positiveRate * 100).toFixed(1)}%`,
        auditor.averageNegatives.toFixed(2),
        auditor.lastAuditAt ? new Date(auditor.lastAuditAt).toISOString() : 'N/A'
      );
    });
    rows.push('');

    pushRow('Audit ID', 'Claim ID', 'Auditor', 'Audited at', 'Negative answers', 'Notes', 'Top findings');
    analysis.topFindings.forEach(item => {
      const findings = item.topFindings.map(f => `${f.section}: ${f.question}`).join(' | ');
      pushRow(item.auditId, item.claimId, item.auditedBy, item.auditedAt, item.negativeAnswers, item.notes || '', findings);
    });

    return rows.join('\r\n');
  };

  const resetExportState = () => {
    if (exportTimerRef.current) {
      window.clearInterval(exportTimerRef.current);
      exportTimerRef.current = null;
    }
    if (exportDownloadUrl) {
      URL.revokeObjectURL(exportDownloadUrl);
      setExportDownloadUrl(null);
    }
  };

const buildNegativeCsv = (rowsSource: NegativeAnswerRow[]) => {
  const rows: string[] = [];
  const escape = (value: string | number | undefined | null) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

    const pushRow = (...cols: Array<string | number | undefined | null>) => {
      rows.push(cols.map(escape).join(','));
    };

    pushRow('Audit ID', 'Claim ID', 'Auditor', 'Audited At', 'Question', 'Section', 'Answer', 'Notes');

    rowsSource.forEach(row => {
      pushRow(
        row.auditId,
        row.claimId,
        row.auditedBy,
        row.auditedAt,
        row.question,
        row.section,
        row.answer,
        row.notes || ''
      );
    });

  return rows.join('\r\n');
};

const buildNegativeSummaryCsv = (summary: AuditAnalysisNegativesResponse['summary']) => {
  const rows: string[] = [];
  rows.push('Claim Summary');
  rows.push(`Total Claims,${summary?.totalClaims ?? 0}`);
  rows.push('');
  rows.push('Claim ID');
  (summary?.claims || []).forEach(claimId => rows.push(claimId));
  return rows.join('\r\n');
};

  const handleExport = async (mode: 'full' | 'negatives') => {
    if (exportStatus === 'in-progress') return;

    resetExportState();
    setExportStatus('in-progress');
    setExportProgress(5);
    setExportMessage(mode === 'negatives' ? 'Preparing negative audit export...' : 'Preparing audit analysis export...');
    setExportError('');
    setExportMode(mode);

    exportTimerRef.current = window.setInterval(() => {
      setExportProgress(prev => {
        if (prev >= 90) return prev;
        const increment = 5 + Math.random() * 10;
        return Math.min(prev + increment, 90);
      });
    }, 500);

    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.auditor) params.append('auditor', filters.auditor);

      const hasFilters = !!(filters.startDate || filters.endDate || filters.auditor);

      if (mode === 'negatives') {
        const url = `/ui/admin/analytics/audit-analysis/negatives${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await apiRequest<AuditAnalysisNegativesResponse>({ url, method: 'GET' });
        const detailCsv = buildNegativeCsv(response.rows || []);
        const summaryCsv = buildNegativeSummaryCsv(response.summary);

        const summaryBlob = new Blob([summaryCsv], { type: 'text/csv;charset=utf-8;' });
        const detailBlob = new Blob([detailCsv], { type: 'text/csv;charset=utf-8;' });

        const summaryUrl = URL.createObjectURL(summaryBlob);
        const detailUrl = URL.createObjectURL(detailBlob);

        setExportDownloadUrl(detailUrl);
        setExportProgress(100);
        setExportMessage(`Export ready. ${response.count} negative answers found.`);
        setExportStatus('completed');

        const dateSuffix = new Date().toISOString().split('T')[0];

        const triggerDownload = (url: string, filename: string) => {
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };

        triggerDownload(summaryUrl, `audit-analysis-negative-summary-${dateSuffix}.csv`);
        triggerDownload(detailUrl, `audit-analysis-negative-answers-${dateSuffix}.csv`);

        setTimeout(() => {
          URL.revokeObjectURL(summaryUrl);
        }, 1000);
      } else {
        const basePath = hasFilters ? '/ui/admin/analytics/audit-analysis' : '/ui/admin/analytics/audit-analysis/cached';
        const url = `${basePath}${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await apiRequest<AuditAnalysisResponse>({ url, method: 'GET' });

        const csv = buildCsv(response);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = URL.createObjectURL(blob);
        setExportDownloadUrl(downloadUrl);
        setExportProgress(100);
        setExportMessage('Export ready. Download should begin automatically.');
        setExportStatus('completed');

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `audit-analysis-full-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err: any) {
      setExportStatus('error');
      setExportError(err?.message || 'Failed to export audit analysis');
      setExportMessage('');
    } finally {
      if (exportTimerRef.current) {
        window.clearInterval(exportTimerRef.current);
        exportTimerRef.current = null;
      }
    }
  };

  const dismissExportStatus = () => {
    resetExportState();
    setExportStatus('idle');
    setExportProgress(0);
    setExportMessage('');
    setExportError('');
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Audit analysis unavailable
        </Typography>
        <Typography color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const refreshButton = (
    <Tooltip title="Refresh">
      <span>
        <IconButton onClick={() => fetchData(filters)} disabled={isRefreshing}>
          <RefreshIcon />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Audit Analysis
          </Typography>
          <Typography color="text.secondary">
            Last generated {new Date(data.generatedAt).toLocaleString('en-GB')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => handleExport('full')}
            disabled={exportStatus === 'in-progress'}
          >
            {exportStatus === 'in-progress' && exportMode === 'full' ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleExport('negatives')}
            disabled={exportStatus === 'in-progress'}
          >
            {exportStatus === 'in-progress' && exportMode === 'negatives' ? 'Exporting…' : 'Export With "No" Answers'}
          </Button>
          {refreshButton}
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
            alignItems: 'center',
          }}
        >
          <TextField
            label="Start date"
            type="date"
            value={filters.startDate}
            onChange={handleFilterChange('startDate')}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End date"
            type="date"
            value={filters.endDate}
            onChange={handleFilterChange('endDate')}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Auditor"
            value={filters.auditor}
            onChange={handleFilterChange('auditor')}
            select
            fullWidth
          >
            <MenuItem value="">All auditors</MenuItem>
            {availableAuditors.map(email => (
              <MenuItem key={email} value={email}>
                {email}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" color="primary" fullWidth onClick={applyFilters}>
              Apply
            </Button>
            <Button variant="outlined" fullWidth onClick={clearFilters}>
              Clear
            </Button>
          </Box>
        </Box>
      </Paper>

      {exportStatus !== 'idle' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {exportStatus === 'completed' && 'Export complete'}
              {exportStatus === 'in-progress' && 'Export in progress'}
              {exportStatus === 'error' && 'Export failed'}
            </Typography>
            <Button onClick={dismissExportStatus} size="small">Dismiss</Button>
          </Box>

          {exportStatus === 'in-progress' && (
            <Box>
              <Typography color="text.secondary" sx={{ mb: 1 }}>{exportMessage}</Typography>
              <LinearProgress variant="determinate" value={exportProgress} sx={{ height: 10, borderRadius: 5 }} />
              <Typography sx={{ mt: 1 }}>
                {Math.round(exportProgress)}%
              </Typography>
            </Box>
          )}

          {exportStatus === 'completed' && (
            <Box>
              <Typography color="success.main" sx={{ mb: 1 }}>{exportMessage}</Typography>
              <Typography variant="body2" color="text.secondary">
                File name: audit-analysis-{exportMode === 'negatives' ? 'negative-answers' : 'full'}-{new Date().toISOString().split('T')[0]}.csv
              </Typography>
              {exportDownloadUrl && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = exportDownloadUrl;
                    link.download = `audit-analysis-${exportMode === 'negatives' ? 'negative-answers' : 'full'}-${new Date().toISOString().split('T')[0]}.csv`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Download again
                </Button>
              )}
            </Box>
          )}

          {exportStatus === 'error' && (
            <Box>
              <Typography color="error">{exportError}</Typography>
            </Box>
          )}
        </Paper>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total audits
            </Typography>
            <Typography variant="h4">{data.totals.audits.toLocaleString()}</Typography>
            <Typography color="text.secondary">Across {data.totals.questions.toLocaleString()} questions</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Positive rate
            </Typography>
            <Typography variant="h4">{formatPercent(data.totals.positiveRate)}</Typography>
            <Typography color="text.secondary">Average negatives per audit {data.totals.averageNegativesPerAudit.toFixed(2)}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Document coverage
            </Typography>
            <Typography variant="h4">{formatPercent(data.documents.documentCoverage)}</Typography>
            <Typography color="text.secondary">Missing documents {data.documents.missingDocuments}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Neutral answers
            </Typography>
            <Typography variant="h4">{data.totals.neutral.toLocaleString()}</Typography>
            <Typography color="text.secondary">Audits impacted {data.documents.auditsWithNeutralAnswers.toLocaleString()}</Typography>
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
          mt: 1,
        }}
      >
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Positive vs negative trend</Typography>
              {isRefreshing && <CircularProgress size={20} />}
            </Box>
            <Box sx={{ width: '100%', height: 360 }}>
              {data.timeSeries.length ? (
                <LineChart
                  series={[
                    {
                      data: data.timeSeries.map(item => item.positive),
                      label: 'Positive answers',
                      color: theme.palette.success.main,
                    },
                    {
                      data: data.timeSeries.map(item => item.negative),
                      label: 'Negative answers',
                      color: theme.palette.error.main,
                    },
                  ]}
                  xAxis={[{
                    data: data.timeSeries.map(item => item.date),
                    scaleType: 'point',
                    valueFormatter: formatDateLabel,
                  }]}
                  yAxis={[{ label: 'Count', min: 0 }]}
                  margin={{ left: 50, right: 20, top: 40, bottom: isMobile ? 70 : 40 }}
                  slotProps={{
                    legend: {
                      position: { vertical: 'top', horizontal: 'center' },
                      direction: isMobile ? 'vertical' : 'horizontal',
                    },
                  }}
                />
              ) : (
                <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No trend data for selected filters</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Section performance
            </Typography>
            <Box sx={{ width: '100%', height: 360 }}>
              {data.sections.length ? (
                <BarChart
                  layout="horizontal"
                  series={[
                    {
                      data: data.sections.map(section => section.positive),
                      label: 'Positive',
                      color: theme.palette.success.main,
                    },
                    {
                      data: data.sections.map(section => section.negative),
                      label: 'Negative',
                      color: theme.palette.error.main,
                    },
                    {
                      data: data.sections.map(section => section.neutral),
                      label: 'Neutral',
                      color: theme.palette.warning.main,
                    },
                  ]}
                  yAxis={[{
                    data: data.sections.map(section => section.section),
                    scaleType: 'band',
                  }]}
                  xAxis={[{ label: 'Responses', min: 0 }]}
                  margin={{ left: 80, right: 20, top: 40, bottom: 40 }}
                />
              ) : (
                <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No section data for selected filters</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' },
          mt: 1,
        }}
      >
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Auditor overview
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Auditor</TableCell>
                <TableCell align="right">Audits</TableCell>
                <TableCell align="right">Positive rate</TableCell>
                <TableCell align="right">Avg negatives</TableCell>
                <TableCell align="right">Last audit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.auditors.map(auditor => (
                <TableRow key={auditor.auditedBy}>
                  <TableCell>{auditor.auditedBy}</TableCell>
                  <TableCell align="right">{auditor.audits}</TableCell>
                  <TableCell align="right">{formatPercent(auditor.positiveRate)}</TableCell>
                  <TableCell align="right">{auditor.averageNegatives.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {auditor.lastAuditAt ? new Date(auditor.lastAuditAt).toLocaleDateString('en-GB') : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' },
          mt: 1,
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Top findings (by negative responses)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Audit</TableCell>
                <TableCell>Claim</TableCell>
                <TableCell>Auditor</TableCell>
                <TableCell align="right">Negatives</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.topFindings.map(item => (
                <TableRow key={item.auditId}>
                  <TableCell>{item.auditId}</TableCell>
                  <TableCell>{item.claimId}</TableCell>
                  <TableCell>{item.auditedBy}</TableCell>
                  <TableCell align="right">{item.negativeAnswers}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {item.topFindings.map(f => `${f.section}: ${f.question}`).join('; ')}
                    </Typography>
                    {item.notes && (
                      <Typography variant="caption" color="text.secondary">{item.notes}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Question hotspots
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Question</TableCell>
                <TableCell align="right">Negatives</TableCell>
                <TableCell align="right">Positive rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.questions.slice(0, 10).map(question => (
                <TableRow key={`${question.section}-${question.question}`}>
                  <TableCell>
                    <Typography variant="body2">{question.question}</Typography>
                    <Typography variant="caption" color="text.secondary">{question.section}</Typography>
                  </TableCell>
                  <TableCell align="right">{question.negative}</TableCell>
                  <TableCell align="right">{formatPercent(question.positiveRate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
};

export default AuditAnalysis;
